import { useState } from 'react'
import TicketTypeGuide from './TicketTypeGuide'
import PriorityGuide from './PriorityGuide'

/** Pestañas del panel de ayuda del formulario de creación. */
const TABS = [
  { key: 'types', label: 'Tipos', icon: 'fa-layer-group' },
  { key: 'priority', label: 'Prioridad', icon: 'fa-gauge-high' },
]

/**
 * Panel de ayuda del formulario de creación de tickets. Agrupa en pestañas la
 * guía de tipos de registro y la de impacto/urgencia para no alargar la barra
 * lateral con dos tarjetas independientes.
 *
 * Además define los estilos compartidos (`.hg-*`) que usan ambas guías, de modo
 * que las dos se lean como una sola pieza: selector segmentado arriba y filas
 * desplegables con ejemplos abajo.
 *
 * @component
 * @returns {JSX.Element}
 */
const TicketHelpPanel = () => {
  const [tab, setTab] = useState('types')

  return (
    <div className="thp">
      <div className="thp-head">
        <span className="thp-head-icon">
          <i className="fa-solid fa-circle-question" />
        </span>
        <span>Ayuda para completar el ticket</span>
      </div>

      <div className="thp-tabs" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={tab === t.key}
            className={`thp-tab ${tab === t.key ? 'is-active' : ''}`}
            onClick={() => setTab(t.key)}
          >
            <i className={`fa-solid ${t.icon}`} />
            {t.label}
          </button>
        ))}
      </div>

      <div className="thp-body">
        {tab === 'types' ? <TicketTypeGuide /> : <PriorityGuide />}
      </div>

      <style>{`
        .thp-head {
          display: flex;
          align-items: center;
          gap: 9px;
          padding: 14px 16px 12px;
          font-size: 0.82rem;
          font-weight: 600;
          color: var(--gm-text);
        }
        .thp-head-icon {
          width: 28px;
          height: 28px;
          flex-shrink: 0;
          border-radius: 9px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 0.8rem;
          color: var(--gm-primary);
          background: var(--gm-primary-soft);
        }

        .thp-tabs {
          display: flex;
          gap: 4px;
          margin: 0 16px;
          padding: 4px;
          background: var(--gm-surface-2);
          border-radius: 10px;
        }
        .thp-tab {
          flex: 1;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          border: none;
          background: transparent;
          padding: 7px 6px;
          border-radius: 7px;
          font-size: 0.8rem;
          font-weight: 500;
          color: var(--gm-text-muted);
          transition: all var(--gm-transition);
        }
        .thp-tab i { font-size: 0.75rem; }
        .thp-tab:hover { color: var(--gm-text); }
        .thp-tab.is-active {
          background: var(--gm-surface);
          color: var(--gm-primary);
          font-weight: 600;
          box-shadow: var(--gm-shadow-sm);
        }

        .thp-body { padding: 14px 16px 16px; }

        /* ── Estilos compartidos por ambas guías ───────────────────────────── */

        .hg-intro {
          font-size: 0.78rem;
          line-height: 1.45;
          color: var(--gm-text-muted);
          margin: 0 0 12px;
        }
        .hg-intro strong { color: var(--gm-text); font-weight: 600; }

        /* Selector de nivel superior (familia / eje) */
        .hg-seg {
          display: flex;
          gap: 6px;
          margin-bottom: 12px;
        }
        .hg-seg-btn {
          flex: 1;
          min-width: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          padding: 8px 6px;
          border: 1px solid var(--gm-border);
          border-radius: 10px;
          background: transparent;
          font-size: 0.78rem;
          font-weight: 500;
          color: var(--gm-text-muted);
          transition: all var(--gm-transition);
        }
        .hg-seg-btn i { font-size: 0.78rem; }
        .hg-seg-btn:hover { color: var(--gm-text); }
        .hg-seg-btn.is-active { font-weight: 600; }

        /* Filas desplegables */
        .hg-rows { display: flex; flex-direction: column; gap: 6px; }
        .hg-row {
          border: 1px solid var(--gm-border);
          border-radius: 10px;
          overflow: hidden;
          transition: border-color var(--gm-transition), background var(--gm-transition);
        }
        .hg-row:hover { border-color: var(--gm-primary); }
        .hg-row-btn {
          display: flex;
          align-items: center;
          gap: 9px;
          width: 100%;
          padding: 9px 10px;
          border: none;
          background: transparent;
          text-align: left;
        }
        .hg-row-icon {
          width: 26px;
          height: 26px;
          flex-shrink: 0;
          border-radius: 8px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 0.78rem;
        }
        .hg-row-texts {
          display: flex;
          flex-direction: column;
          min-width: 0;
          flex: 1;
        }
        .hg-row-label {
          font-size: 0.82rem;
          font-weight: 600;
          color: var(--gm-text);
          line-height: 1.2;
        }
        .hg-row-hint {
          font-size: 0.72rem;
          line-height: 1.3;
          color: var(--gm-text-muted);
        }
        .hg-chevron {
          flex-shrink: 0;
          font-size: 0.65rem;
          color: var(--gm-text-faint);
          transition: transform var(--gm-transition);
        }
        .hg-chevron.is-open { transform: rotate(180deg); }

        .hg-examples {
          list-style: none;
          margin: 0;
          padding: 0 10px 10px 45px;
          display: flex;
          flex-direction: column;
          gap: 6px;
        }
        .hg-examples li {
          position: relative;
          font-size: 0.75rem;
          line-height: 1.35;
          color: var(--gm-text-muted);
          padding-left: 10px;
        }
        .hg-examples li::before {
          content: '';
          position: absolute;
          left: 0;
          top: 0.5em;
          width: 4px;
          height: 4px;
          border-radius: 50%;
          background: currentColor;
          opacity: 0.5;
        }
      `}</style>
    </div>
  )
}

export default TicketHelpPanel
